import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiStar, FiMapPin, FiPackage, FiClock } from 'react-icons/fi';
import api from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { formatCurrency } from '@/utils/formatters';
import ProductCard from '@/components/cards/ProductCard';
import LoadingSpinner from '@/components/common/LoadingSpinner';

const StoreDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const [store, setStore] = useState(null);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('products');

  useEffect(() => {
    loadStoreData();
  }, [id]);

  const loadStoreData = async () => {
    try {
      const [storeRes, productsRes, reviewsRes] = await Promise.all([
        api.get(`/stores/${id}`),
        api.get('/products', { params: { store_id: id } }),
        api.get(`/reviews/store/${id}`)
      ]);
      
      setStore(storeRes.data);
      setProducts(productsRes.data);
      setReviews(reviewsRes.data);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (product) => {
    addToCart(product);
  };

  const handleProductClick = (product) => {
    navigate(`/cliente/produto/${product.id}`);
  };

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-0.5">
        {[1, 2, 3, 4, 5].map((star) => (
          <FiStar
            key={star}
            size={16}
            className={star <= rating ? 'text-yellow-400 fill-current' : 'text-gray-300'}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!store) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Loja não encontrada</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#1A1A1A] to-[#DC143C] text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors mb-4"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 bg-white rounded-xl flex items-center justify-center text-3xl font-bold text-[#DC143C]">
              {store.name[0]}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{store.name}</h1>
              <div className="flex items-center gap-2 mt-1">
                <FiMapPin size={14} />
                <span className="text-sm text-white/80">{store.address}</span>
              </div>
              <div className="flex items-center gap-4 mt-2">
                <div className="flex items-center gap-1">
                  <FiStar className="text-yellow-400 fill-current" size={16} />
                  <span className="font-semibold">{store.rating?.toFixed(1) || '0.0'}</span>
                  <span className="text-white/60 text-sm">({reviews.length} avaliações)</span>
                </div>
                <div className="flex items-center gap-1 text-white/60 text-sm">
                  <FiPackage size={14} />
                  <span>{products.length} produtos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Tabs */}
      <div className="bg-white shadow-sm sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab('products')}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === 'products'
                  ? 'border-[#DC143C] text-[#DC143C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Produtos ({products.length})
            </button>
            <button
              onClick={() => setActiveTab('reviews')}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === 'reviews'
                  ? 'border-[#DC143C] text-[#DC143C]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Avaliações ({reviews.length})
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {activeTab === 'products' ? (
          /* Products Grid */
          products.length === 0 ? (
            <div className="text-center py-12">
              <FiPackage className="mx-auto text-5xl text-gray-300 mb-4" />
              <p className="text-gray-500">Nenhum produto disponível</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  onAddToCart={handleAddToCart}
                  onClick={() => handleProductClick(product)}
                />
              ))}
            </div>
          )
        ) : (
          /* Reviews List */
          <div className="space-y-4">
            {/* Rating Summary */}
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="flex items-center gap-6">
                <div className="text-center">
                  <div className="text-5xl font-bold text-[#DC143C]">
                    {store.rating?.toFixed(1) || '0.0'}
                  </div>
                  <div className="mt-2">{renderStars(Math.round(store.rating || 0))}</div>
                  <p className="text-sm text-gray-500 mt-1">{reviews.length} avaliações</p>
                </div>
                
                {/* Rating Distribution */}
                <div className="flex-1 space-y-2">
                  {[5, 4, 3, 2, 1].map((star) => {
                    const count = reviews.filter(r => r.store_rating === star).length;
                    const percentage = reviews.length > 0 ? (count / reviews.length) * 100 : 0;
                    return (
                      <div key={star} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 w-3">{star}</span>
                        <FiStar size={12} className="text-yellow-400 fill-current" />
                        <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-yellow-400 rounded-full"
                            style={{ width: `${percentage}%` }}
                          />
                        </div>
                        <span className="text-sm text-gray-500 w-8">{count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Reviews */}
            {reviews.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-xl">
                <FiStar className="mx-auto text-5xl text-gray-300 mb-4" />
                <p className="text-gray-500">Nenhuma avaliação ainda</p>
                <p className="text-sm text-gray-400 mt-1">Seja o primeiro a avaliar esta loja!</p>
              </div>
            ) : (
              reviews.map((review) => (
                <div key={review.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center text-gray-600 font-medium">
                        {review.client_name?.[0] || 'C'}
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{review.client_name || 'Cliente'}</p>
                        <div className="flex items-center gap-2">
                          {renderStars(review.store_rating)}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 text-sm text-gray-400">
                      <FiClock size={12} />
                      <span>{formatDate(review.created_at)}</span>
                    </div>
                  </div>
                  {review.store_comment && (
                    <p className="mt-3 text-gray-600">{review.store_comment}</p>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default StoreDetail;
